import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CouponsAPI } from '../../../../api/payments/coupons/api';
import { PlansAPI } from '../../../../api/payments/plans/api';
import { AddonsAPI } from '../../../../api/payments/addons/api';
import {
  CouponScopeEnum,
  CouponValueTypeEnum,
} from '../../../../api/payments/coupons/domain/enum';
import {
  CouponScopeLabels,
  CouponValueTypeLabels,
} from '../../../../api/payments/coupons/domain/labels';
import { toDateInputValue, dateInputValueToIso } from '../../../../helpers/dateInput';

const CYCLES_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);

const VALUE_TYPE_OPTIONS = [
  {
    value: CouponValueTypeEnum.PERCENT,
    label: CouponValueTypeLabels[CouponValueTypeEnum.PERCENT],
  },
  {
    value: CouponValueTypeEnum.FIXED,
    label: CouponValueTypeLabels[CouponValueTypeEnum.FIXED],
  },
];

// O select de criação/edição usa um rótulo mais curto para ADDON_ITEM
// do que a exibição somente-leitura (CouponScopeLabels.ADDON_ITEM).
const APPLIES_TO_OPTIONS = [
  { value: CouponScopeEnum.ALL, label: CouponScopeLabels[CouponScopeEnum.ALL] },
  { value: CouponScopeEnum.PLAN, label: CouponScopeLabels[CouponScopeEnum.PLAN] },
  { value: CouponScopeEnum.ADDON_ITEM, label: 'Produto' },
  {
    value: CouponScopeEnum.ADDON_TOTAL,
    label: CouponScopeLabels[CouponScopeEnum.ADDON_TOTAL],
  },
];

function emptyRule() {
  return {
    id: null,
    valueType: CouponValueTypeEnum.PERCENT,
    value: '',
    scope: CouponScopeEnum.ALL,
    planId: '',
    addonId: '',
  };
}

function mapDiscountToRule(discount) {
  return {
    id: discount.id ?? null,
    valueType:
      discount.valueType ?? discount.value_type ?? CouponValueTypeEnum.PERCENT,
    value: discount.value ?? '',
    scope: discount.scope ?? CouponScopeEnum.ALL,
    planId: discount.planId ?? '',
    addonId: discount.addonId ?? '',
  };
}

function ruleForApi(rule) {
  return {
    valueType: rule.valueType,
    value: Number(rule.value),
    scope: rule.scope,
    planId: rule.scope === CouponScopeEnum.PLAN ? rule.planId : '',
    addonId:
      rule.scope === CouponScopeEnum.ADDON_ITEM ||
      rule.scope === CouponScopeEnum.ADDON_TOTAL
        ? rule.addonId
        : '',
  };
}

function rulesAreEqual(a, b) {
  return (
    a.valueType === b.valueType &&
    Number(a.value) === Number(b.value) &&
    a.scope === b.scope &&
    (a.planId || '') === (b.planId || '') &&
    (a.addonId || '') === (b.addonId || '')
  );
}

export function useAddCouponBootstrap(couponId, initialCoupon) {
  const history = useHistory();
  const originalRulesRef = useRef([]);
  const [notFound, setNotFound] = useState(false);
  const [couponFromState, setCouponFromState] = useState(initialCoupon ?? null);
  const [fetchingCoupon, setFetchingCoupon] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [addons, setAddons] = useState([]);
  const [addonsLoading, setAddonsLoading] = useState(true);

  useEffect(() => {
    if (!couponId || couponFromState) return;
    setFetchingCoupon(true);
    CouponsAPI.get(couponId)
      .then((response) => setCouponFromState(response.data))
      .catch(() => {
        toast.warn(
          'Não foi possível carregar os dados do cupom, tente novamente pela listagem.',
        );
        setNotFound(true);
      })
      .finally(() => setFetchingCoupon(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponId]);

  // Busca compartilhada, uma única vez: evita que cada linha de regra
  // (PlanSelect/AddonSelect) dispare sua própria requisição da lista inteira.
  useEffect(() => {
    PlansAPI.list()
      .then((response) => setPlans(response.data.content || []))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
    AddonsAPI.list()
      .then((response) => setAddons(response.data.content || []))
      .catch(() => setAddons([]))
      .finally(() => setAddonsLoading(false));
  }, []);

  const initialValues = {
    code: couponFromState?.code ?? '',
    active: couponFromState?.active ?? true,
    cyclesCount: couponFromState?.cyclesCount ?? 1,
    startsAt:
      toDateInputValue(couponFromState?.startsAt) ||
      toDateInputValue(new Date()),
    expiresAt: toDateInputValue(couponFromState?.expiresAt),
    description: couponFromState?.observation ?? '',
    rules:
      couponFromState?.discounts?.length > 0
        ? couponFromState.discounts.map(mapDiscountToRule)
        : [emptyRule()],
  };

  originalRulesRef.current = initialValues.rules.filter((rule) => rule.id);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: yup.object().shape({
      code: yup.string().required('O campo nome do cupom é obrigatório!'),
      cyclesCount: yup
        .number()
        .typeError('Selecione o número de ciclos')
        .required('O campo ciclos aplicáveis é obrigatório!'),
      startsAt: yup.string().required('A data de início é obrigatória!'),
      expiresAt: yup.string().required('A data de expiração é obrigatória!'),
      active: yup.boolean().required('O campo ativo é obrigatório!'),
      description: yup.string(),
      rules: yup.array().of(
        yup.object().shape({
          value: yup
            .number()
            .typeError('Informe um valor válido')
            .required('O valor do desconto é obrigatório!'),
          valueType: yup.string().when('scope', {
            is: CouponScopeEnum.ADDON_ITEM,
            then: (schema) =>
              schema.oneOf(
                [CouponValueTypeEnum.FIXED],
                'Produto (a cada unidade) só aceita valor fixo',
              ),
          }),
          planId: yup.string(),
          addonId: yup.string().when('scope', {
            is: CouponScopeEnum.ADDON_ITEM,
            then: (schema) => schema.required('Informe o produto'),
          }),
          scope: yup
            .string()
            .required('Defina a regra de aplicação de desconto!'),
        }),
      ),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        const couponPayload = {
          code: values.code,
          active: values.active,
          startsAt: dateInputValueToIso(values.startsAt, false),
          description: values.description,
          cyclesCount: Number(values.cyclesCount),
          expiresAt: dateInputValueToIso(values.expiresAt),
        };

        let id = couponId;
        if (couponId) {
          await CouponsAPI.update(couponId, couponPayload);
        } else {
          const response = await CouponsAPI.create(couponPayload);
          id = response.data.id;
        }

        const originalRules = originalRulesRef.current;
        const currentRules = values.rules;

        const rulesToCreate = currentRules.filter((rule) => !rule.id);
        const rulesToDelete = originalRules.filter(
          (original) => !currentRules.some((rule) => rule.id === original.id),
        );
        const rulesToUpdate = currentRules.filter((rule) => {
          if (!rule.id) return false;
          const original = originalRules.find((o) => o.id === rule.id);
          return original && !rulesAreEqual(original, rule);
        });

        const [createdResponses] = await Promise.all([
          Promise.all(
            rulesToCreate.map((rule) => CouponsAPI.createRule(id, ruleForApi(rule))),
          ),
          ...rulesToUpdate.map((rule) =>
            CouponsAPI.updateRule(id, rule.id, ruleForApi(rule)),
          ),
          ...rulesToDelete.map((rule) => CouponsAPI.deleteRule(id, rule.id)),
        ]);

        const keptRules = currentRules
          .filter((rule) => rule.id)
          .map((rule) => ({ ...ruleForApi(rule), id: rule.id }));
        const createdRules = rulesToCreate.map((rule, i) => ({
          ...ruleForApi(rule),
          id: createdResponses[i]?.data?.id ?? null,
        }));

        const coupon = {
          id,
          code: values.code,
          active: values.active,
          cyclesCount: Number(values.cyclesCount),
          startsAt: couponPayload.startsAt,
          expiresAt: couponPayload.expiresAt,
          observation: values.description,
          discounts: [...keptRules, ...createdRules],
        };

        toast.success('Cupom salvo com sucesso!');
        history.push(`/payments/coupons/${id}`, { coupon });
      } catch (error) {
        // erros específicos já são exibidos via toast pelas funções de api
      } finally {
        setSubmitting(false);
      }
    },
  });

  const addRule = () => {
    formik.setFieldValue('rules', [...formik.values.rules, emptyRule()]);
  };

  const removeRule = (index) => {
    const rules = formik.values.rules.filter((_, i) => i !== index);
    formik.setFieldValue('rules', rules.length > 0 ? rules : [emptyRule()]);
  };

  const updateRule = (index, field, value) => {
    const rules = formik.values.rules.map((rule, i) =>
      i === index ? { ...rule, [field]: value } : rule,
    );
    formik.setFieldValue('rules', rules);
  };

  const handleCancel = () => history.push('/payments/coupons');

  return {
    notFound,
    fetchingCoupon,
    formik,
    plans,
    plansLoading,
    addons,
    addonsLoading,
    addRule,
    removeRule,
    updateRule,
    handleCancel,
    valueTypeOptions: VALUE_TYPE_OPTIONS,
    appliesToOptions: APPLIES_TO_OPTIONS,
    cyclesOptions: CYCLES_OPTIONS,
  };
}
